import SailboatOffshore16 from '@carbon/icons-react/es/sailboat--offshore/16';
import { List, Tooltip, Typography } from 'antd';
import React, { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { TimeoffResponse } from 'api/timeoff/types';
import { Avatar } from 'components/atoms/avatar';
import { TimeOffWrapper } from 'components/atoms/select/custom-components';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import {
  ClickEventTags,
  useLoadLoggerForDataDog,
} from 'hooks/use-data-dog-logger';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { Suggestion } from 'vendor/ckeditor/plugins/mention/types';
import { setActiveSuggestion, useMentions } from '../mention-store';
import styles from './suggestion-list-item.module.css';

const { Paragraph, Text } = Typography;

interface SuggestionListItemProps extends Suggestion {
  onSelect: (selected: Suggestion) => void;
  isNotInvited?: boolean;
  timeOffArray?: TimeoffResponse[];
}

const Item = styled(List.Item)`
  padding: var(--spacing-03) var(--spacing-04);
  border: none;
  cursor: pointer;
  transition: background var(--animation-duration-medium);
`;

const StyledText = styled(Text)`
  color: var(--orange-40);
`;

export const SuggestionListItem: FC<SuggestionListItemProps> = ({
  name,
  nameEle,
  photoUrl,
  logoUrl,
  avatar,
  onSelect,
  type,
  subType,
  objectId,
  projectId,
  spaceId,
  isNotInvited = false,
  timeOffArray,
}) => {
  const [contextState, contextDispatch] = useMentions();

  useLoadLoggerForDataDog([ClickEventTags.CkMentionPopup]);
  const { activeSuggestion } = contextState;
  const avatarComponent = avatar || (
    <Avatar name={name} src={photoUrl} companyLogoUrl={logoUrl} />
  );
  const suggestionItem: Suggestion = {
    name,
    photoUrl,
    avatar,
    type,
    subType,
    objectId,
    projectId,
    spaceId,
  };

  const hasTimeOffToday = checkIfCurrentDateHasTimeOff(timeOffArray);

  const timeOffText = (
    <GetTimeOffTooltipContent timeOff={hasTimeOffToday} userName={name} />
  );

  return (
    <Item
      className={`${
        activeSuggestion?.objectId === objectId &&
        activeSuggestion?.type === type
          ? `${styles.active} mention__item--active`
          : ''
      }`}
      onClick={() => onSelect(suggestionItem)}
      onMouseEnter={() => {
        contextDispatch(setActiveSuggestion(suggestionItem));
      }}
      extra={
        isNotInvited && (
          <StyledText>
            <FormattedMessage id="not_in_project" />
          </StyledText>
        )
      }
    >
      <List.Item.Meta
        className={styles.item}
        avatar={avatarComponent}
        title={
          <TimeOffWrapper>
            {nameEle ?? <Paragraph ellipsis={{ rows: 1 }}>{name}</Paragraph>}
            {hasTimeOffToday && (
              <Tooltip title={timeOffText}>
                <SailboatOffshore16 />
              </Tooltip>
            )}
          </TimeOffWrapper>
        }
      />
    </Item>
  );
};
